import FilterProduct from './filterproduct';
import { round } from 'mathjs';
import { ProductBundle } from './productbundle';

class Filter {
    constructor(json) {
        if (json != null) {
            this.id = json.id;
            if (json.product) this.product = new FilterProduct(json.product);
            else if (json.bundle) this.bundle = new ProductBundle(json.bundle);
            this.count = json.count;
            this.discount = json.discount;
            this.price = json.price;
            this.additionalInfo = json.additionalInfo || '';
            this.viewOrder = json.viewOrder;
            this.originalProductName = json.originalProductName;
            this.contractPricesInUse = json.contractPricesInUse;
            this.unitContractNetPrice = json.unitContractNetPrice;
        } else {
            this.id = 0;
            this.product = new FilterProduct();
            this.count = 0;
            this.discount = 0;
            this.price = null;
            this.additionalInfo = '';
            this.viewOrder = 1;
            this.unitContractNetPrice = 0;
        }
    }

    cpNotInUsePriceRounded() {
        return this.price % 1 > 0 ? round(this.price, 2) : this.price;
    }

    cpNotInUseDiscountRounded(precision) {
        if (!!!precision) precision = 4;
        return this.discount % 1 > 0 ? round(this.discount, precision) : this.discount;
    }

    // exchangeRate converts the (always EUR) catalog price into the tenant's own currency -- see
    // doc/sweden-expansion-currency-plan.md "FX / Exchange Rate Management". Defaults to 1 (no
    // conversion), which is correct both for EUR tenants and for any caller not yet currency-aware.
    cpNotInUseCalculateDiscountPricePerPcs(exchangeRate = 1) {
        const price = round(this.catalogValuePerPcs(exchangeRate) * ((100 - this.discount) / 100), 2);
        if (isNaN(price)) return 0;
        return price;
    }

    cpNotInUseCalculateDiscountPercent(exchangeRate = 1) {
        const discount = round(100 - (this.price / this.catalogValuePerPcs(exchangeRate)) * 100, 4);
        if (isNaN(discount)) return 0;
        return discount;
    }

    getFilterTotalValue(exchangeRate = 1) {
        //returns filter price multiplicated by count
        if (this.contractPricesInUse) {
            return this.unitContractNetPrice * this.count;
        }
        return this.discount === null
            ? this.price * this.count
            : ((100 - this.discount) / 100) * this.catalogValuePerPcs(exchangeRate) * this.count;
    }

    //return filter price, if price set, return rounded price, if discount set, calculates rounded price
    getFilterPriceRounded(exchangeRate = 1) {
        if (this.contractPricesInUse) {
            round(this.unitContractNetPrice, 2);
        }
        return this.discount == null
            ? round(this.price, 2)
            : round(((100 - this.discount) / 100) * this.catalogValuePerPcs(exchangeRate), 2);
    }

    // exchangeRate converts productionPrice() (always EUR, see doc/sweden-expansion-currency-plan.md)
    // into the same currency as getFilterTotalValue(exchangeRate) -- which is either the tenant's own
    // currency (fixed Filter.price) or the FX-converted EUR catalog price (% discount). Without this,
    // a non-EUR tenant's margin mixed EUR production cost with tenant-currency revenue.
    margin(exchangeRate = 1) {
        if (this.productionPrice() > 0) {
            return this.getFilterTotalValue(exchangeRate) - this.productionPrice() * exchangeRate * this.count;
        } else {
            return -1;
        }
    }

    marginPercentage(exchangeRate = 1) {
        if (this.productionPrice() > 0) {
            return (
                (1 - (this.productionPrice() * exchangeRate * this.count) / this.getFilterTotalValue(exchangeRate)) *
                100
            ).toFixed(2);
        } else {
            return -1;
        }
    }

    catalogValue(exchangeRate = 1) {
        if (this.product) {
            return this.product.priceWithoutVat * exchangeRate * this.count;
        } else if (this.bundle) {
            return this.bundle.priceWithoutVat() * exchangeRate * this.count;
        }
    }

    catalogValuePerPcs(exchangeRate = 1) {
        if (this.product) {
            return this.product.priceWithoutVat * exchangeRate;
        } else if (this.bundle) {
            return this.bundle.priceWithoutVat() * exchangeRate;
        }
    }

    productionPrice() {
        if (this.product) {
            return this.product.productionPrice;
        } else {
            return this.bundle.productionPrice();
        }
    }

    expired() {
        if (this.product) {
            return this.product.expired;
        } else {
            return this.bundle.isExpired();
        }
    }

    fabricSurfaceArea() {
        if (this.product) {
            return this.product.fabricSurfaceArea;
        } else {
            return this.bundle.fabricSurfaceArea();
        }
    }
}

export default Filter;
